import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BottomTabBar } from '@/components/ui/BottomTabBar';
import { ProposalSlotBadge } from '@/components/ui/ProposalSlotBadge';
import { useAuthStore } from '@/store/authStore';
import { useData } from '@/hooks/useData';
import { useProposals } from '@/services/children';
import { GlobalHeader } from '@/components/GlobalHeader';
import { useHomeworks } from '@/services/homeworks';
import { isTimetableRequired } from '@/utils/academicReset';

interface ProfileResponse {
  display_name: string | null;
}

interface WeeklyProgressResponse {
  days: unknown[];
  total_minutes: number;
  streak_count: number;
}

interface TimetableEntry {
  subject: string;
  day: string;      // backend stores as lowercase full name: 'monday', 'tuesday'…
  start_time: string;
  end_time: string;
  room: string | null;
}

interface TimetableResponse {
  entries: TimetableEntry[];
}

interface Exam {
  exam_id: string;
  subject: string;
  name: string;    // e.g. "Midterm Exam"
  date: string;    // ISO: "YYYY-MM-DD"
}

interface ExamsResponse {
  exams: Exam[];
}

// Backend DayOfWeek StrEnum uses lowercase full names
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const todayString = DAY_NAMES[new Date().getDay()] ?? 'monday';
const todayISO = new Date().toISOString().split('T')[0] ?? '';

function formatExamDate(iso: string, locale: string): string {
  const parts = iso.split('-');
  const month = parseInt(parts[1] ?? '1', 10);
  const day = parseInt(parts[2] ?? '1', 10);
  const date = new Date(Date.UTC(2000, month - 1, day));
  const monthName = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }).format(date);
  return `${day} ${monthName}`;
}

// Maps backend full day names to i18n key abbreviations (Mon/Tue/…)
const DAY_ABBR: Record<string, string> = {
  sunday: 'Sun', monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
  thursday: 'Thu', friday: 'Fri', saturday: 'Sat',
};

export function StudentDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { childId, displayName, setDisplayName } = useAuthStore();

  // Proposals native scroll-snap carousel — touch/swipe comes for free from the
  // browser; scrollTo() keeps the arrows/dots in sync with swipe-driven navigation.
  const carouselRef = useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const scrollToSlide = (idx: number) => {
    if (!carouselRef.current) return;
    carouselRef.current.scrollTo({ left: idx * carouselRef.current.clientWidth, behavior: 'smooth' });
  };
  const nextSlide = () => scrollToSlide(activeSlide === proposals.length - 1 ? 0 : activeSlide + 1);
  const prevSlide = () => scrollToSlide(activeSlide === 0 ? proposals.length - 1 : activeSlide - 1);
  const handleCarouselScroll = () => {
    if (!carouselRef.current) return;
    const width = carouselRef.current.clientWidth || 1;
    setActiveSlide(Math.round(carouselRef.current.scrollLeft / width));
  };

  // Fetch profile for display name when JWT doesn't carry it (returning login)
  const { data: profileData } = useData<ProfileResponse>(
    childId && !displayName ? `/children/${childId}/profile` : null,
  );
  useEffect(() => {
    if (profileData?.display_name) setDisplayName(profileData.display_name);
  }, [profileData, setDisplayName]);

  const { data, isLoading: proposalsLoading, error } = useProposals(childId ?? '');
  const isLoading = proposalsLoading || !childId;
  const { data: progressData } = useData<WeeklyProgressResponse>(
    childId ? `/children/${childId}/weekly-progress` : null,
  );
  const { data: timetableData, isLoading: timetableLoading } = useData<TimetableResponse>(
    childId ? `/children/${childId}/timetable` : null,
  );
  const { data: examsData } = useData<ExamsResponse>(
    childId ? `/children/${childId}/exams` : null,
  );
  const { data: homeworksData } = useHomeworks(childId ?? '');

  const proposals   = data?.proposals ?? [];
  const streakCount = progressData?.streak_count ?? 0;
  const openHomeworks = (homeworksData?.homeworks ?? []).filter((h) => h.status === 'open');

  // Homework native scroll-snap carousel (same pattern as the proposals carousel above)
  const hwCarouselRef = useRef<HTMLDivElement>(null);
  const [activeHwSlide, setActiveHwSlide] = useState(0);
  const scrollToHwSlide = (idx: number) => {
    if (!hwCarouselRef.current) return;
    hwCarouselRef.current.scrollTo({ left: idx * hwCarouselRef.current.clientWidth, behavior: 'smooth' });
  };
  const nextHwSlide = () =>
    scrollToHwSlide(activeHwSlide === openHomeworks.length - 1 ? 0 : activeHwSlide + 1);
  const prevHwSlide = () =>
    scrollToHwSlide(activeHwSlide === 0 ? openHomeworks.length - 1 : activeHwSlide - 1);
  const handleHwScroll = () => {
    if (!hwCarouselRef.current) return;
    const width = hwCarouselRef.current.clientWidth || 1;
    setActiveHwSlide(Math.round(hwCarouselRef.current.scrollLeft / width));
  };

  const isTimetableEmpty =
    !timetableLoading &&
    (!timetableData || !timetableData.entries || timetableData.entries.length === 0);
  // US-PRF-02: distinguishes "brand-new student, never set up a timetable" (generic
  // hero below) from "just reset their academic year and dropped off before
  // uploading a new one" (higher-priority banner) — same empty signal, different flag.
  const academicResetPending = isTimetableEmpty && isTimetableRequired(childId);

  const todaysClasses = (timetableData?.entries ?? [])
    .filter((e) => e.day === todayString)
    .sort((a, b) => a.start_time.localeCompare(b.start_time));

  const nextExam = (examsData?.exams ?? [])
    .filter((e) => e.date >= todayISO)
    .sort((a, b) => a.date.localeCompare(b.date))[0] ?? null;

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader />
        <div className="app-content">

          {/* ── Header ── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: 0 }}>
              {t('student_dash.greeting', { name: displayName ?? 'Student' })}!
            </h1>
            {streakCount > 0 && (
              <div className="streak-badge">🔥 {t('session_complete.streak', { count: streakCount })}</div>
            )}
          </div>

          {/* ── Upcoming Exam ── */}
          {nextExam && (
            <>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 12px' }}>
                {t('student_dash.upcoming_exam')}
              </h2>
              <div style={{
                background: 'white',
                borderLeft: '4px solid var(--danger)',
                borderRadius: '8px',
                padding: '16px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '24px',
              }}>
                <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>📅</span>
                <div>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-dark)' }}>
                    <strong>{nextExam.subject}</strong>
                    {nextExam.name ? ` ${nextExam.name}` : ''}
                    {' — '}
                    {formatExamDate(nextExam.date, i18n.language)}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* ── Timetable loading skeleton ── */}
          {timetableLoading && (
            <div className="loading-skeleton" style={{ height: '200px', borderRadius: '16px' }} />
          )}

          {/* ── High-priority banner: academic year reset, timetable still missing ── */}
          {academicResetPending && (
            <div style={{
              background: '#FEF2F2',
              border: '1px solid var(--danger)',
              borderRadius: '16px',
              padding: '24px 20px',
              textAlign: 'center',
              marginTop: '24px',
            }}>
              <p style={{ fontSize: '2.5rem', margin: '0 0 12px' }}>⚠️</p>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--danger)', margin: '0 0 8px' }}>
                {t('academic_context.banner_title')}
              </h2>
              <p style={{ color: 'var(--text-dark)', fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>
                {t('academic_context.banner_desc')}
              </p>
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: '100%', marginTop: '20px' }}
                onClick={() => navigate('/student/timetable/scan')}
              >
                📷 {t('student_dash.setup_timetable')}
              </button>
            </div>
          )}

          {/* ── First-time hero: no timetable yet ── */}
          {isTimetableEmpty && !academicResetPending && (
            <div style={{
              background: 'var(--primary-light, #EEF2FF)',
              borderRadius: '16px',
              padding: '32px 20px',
              textAlign: 'center',
              marginTop: '24px',
            }}>
              <p style={{ fontSize: '3rem', margin: '0 0 16px' }}>📅</p>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 12px' }}>
                {t('student_dash.no_timetable_title')}
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>
                {t('student_dash.no_timetable_desc')}
              </p>
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: '100%', marginTop: '24px' }}
                onClick={() => navigate('/student/timetable/edit')}
              >
                ⚙️ {t('student_dash.setup_timetable')}
              </button>
            </div>
          )}

          {/* ── Normal dashboard: timetable exists ── */}
          {!timetableLoading && !isTimetableEmpty && (
            <>
              {/* Up Next */}
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 12px' }}>
                {t('student_dash.up_next')}
              </h2>

              {!isLoading && !error && data?.degraded && (
                <p style={{
                  color: 'var(--warning)',
                  fontSize: '0.8rem',
                  margin: '0 0 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  ⚡ {t('student_dash.degraded_notice')}
                </p>
              )}

              {isLoading && (
                <div className="loading-skeleton" style={{ height: '140px' }} />
              )}

              {!isLoading && error && (
                <p style={{ color: 'var(--danger)', fontSize: '0.9rem', margin: 0 }}>
                  {t('student_dash.load_failed')}
                </p>
              )}

              {!isLoading && !error && proposals.length > 0 && (
                <>
                  {/* Native scroll-snap carousel — touch/swipe works via real browser
                      scrolling; no CSS transform, so it can never stack vertically.
                      Sizing is fully explicit (flex: 0 0 100%, not a Tailwind width
                      utility) so there's no ambiguity in how each slide is measured.
                      flexShrink: 0 — this wrapper is a flex item of .app-content
                      (a height-constrained flex column on desktop, where
                      .phone-mockup is a fixed 812px frame); overflow-hidden gives
                      a flex item an automatic minimum height of 0 per the flexbox
                      spec, so without flexShrink:0 the carousel gets shrunk to
                      nothing instead of the page scrolling. */}
                  <div className="relative overflow-hidden rounded-xl pb-2" style={{ width: '100%', flexShrink: 0 }}>
                    <div
                      ref={carouselRef}
                      className="no-scrollbar"
                      onScroll={handleCarouselScroll}
                      style={{
                        display: 'flex',
                        width: '100%',
                        minWidth: 0,
                        overflowX: 'auto',
                        scrollSnapType: 'x mandatory',
                        scrollBehavior: 'smooth',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                      } as React.CSSProperties}
                    >
                      {proposals.map((p, idx) => (
                        <div
                          key={idx}
                          style={{
                            flex: '0 0 100%',
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            boxSizing: 'border-box',
                            padding: '0 4px',
                            scrollSnapAlign: 'start',
                          }}
                        >
                          <div className="ai-proposal">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <p style={{ fontWeight: 700, color: 'var(--text-dark)', margin: 0 }}>{p.subject}</p>
                              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 }}>
                                {p.duration_minutes} min
                              </p>
                            </div>
                            {p.free_time_slot && (
                              <div style={{ margin: '8px 0 0' }}>
                                <ProposalSlotBadge slot={p.free_time_slot} />
                              </div>
                            )}
                            {p.rationale && (
                              <p style={{ color: 'var(--text-dark)', fontSize: '0.9rem', margin: '10px 0' }}>
                                {p.rationale}
                              </p>
                            )}
                            {p.type === 'homework' && p.homework_id ? (
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => navigate(`/student/homework/${p.homework_id}`)}
                                style={{ fontSize: '0.85rem' }}
                              >
                                📚 {t('student_dash.get_homework_help')}
                              </button>
                            ) : (
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={() => navigate(`/student/quiz?subject=${encodeURIComponent(p.subject)}&theme=${encodeURIComponent(p.subject)}`)}
                                  style={{ flex: 1, fontSize: '0.82rem', padding: '10px 6px' }}
                                >
                                  📝 Quiz
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline"
                                  onClick={() => navigate(`/student/explain?subject=${encodeURIComponent(p.subject)}`)}
                                  style={{ flex: 1, fontSize: '0.82rem', padding: '10px 6px' }}
                                >
                                  💬 {t('student_dash.explain')}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline"
                                  onClick={() => navigate(`/student/flashcards?subject=${encodeURIComponent(p.subject)}`)}
                                  style={{ flex: 1, fontSize: '0.82rem', padding: '10px 6px' }}
                                >
                                  🎴 {t('student_dash.cards_btn')}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Navigation arrows */}
                    {proposals.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={prevSlide}
                          className="absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/90 border border-slate-200 text-slate-800 rounded-full shadow-sm hover:bg-slate-50 z-10"
                          aria-label={t('actions.previous')}
                        >
                          &#8592;
                        </button>
                        <button
                          type="button"
                          onClick={nextSlide}
                          className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/90 border border-slate-200 text-slate-800 rounded-full shadow-sm hover:bg-slate-50 z-10"
                          aria-label={t('actions.next')}
                        >
                          &#8594;
                        </button>
                      </>
                    )}
                  </div>

                  {/* Dot indicators */}
                  {proposals.length > 1 && (
                    <div className="flex justify-center gap-1.5 mt-2">
                      {proposals.map((_, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => scrollToSlide(idx)}
                          style={{
                            width: activeSlide === idx ? '20px' : '8px',
                            height: '8px',
                            borderRadius: '4px',
                            border: 'none',
                            background: activeSlide === idx ? 'var(--primary)' : '#D1D5DB',
                            transition: 'all 0.2s',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {!isLoading && !error && proposals.length === 0 && (
                <div className="ai-proposal" style={{ textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: '0 0 12px' }}>
                    {t('student_dash.no_proposals')}
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate('/student/confidence')}
                  >
                    {t('student_dash.rate_subjects')}
                  </button>
                </div>
              )}

              {/* ── Homework ── */}
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '24px 0 12px' }}>
                {t('student_dash.homework')}
              </h2>

              {/* Add Homework — always visible */}
              <Link
                to="/student/homework/add"
                className="btn btn-outline"
                style={{ display: 'block', width: '100%', boxSizing: 'border-box', textAlign: 'center', textDecoration: 'none', marginBottom: '12px', fontSize: '0.9rem' }}
              >
                {t('homework.add_btn')}
              </Link>

              {openHomeworks.length > 0 ? (
                <>
                  <div className="relative overflow-hidden rounded-xl pb-2" style={{ width: '100%', flexShrink: 0 }}>
                    {/* Native scroll-snap carousel — same fix as the proposals carousel above.
                        flexShrink: 0 on the wrapper — see the proposals carousel's comment above
                        for why overflow-hidden otherwise lets this collapse to 0 height on desktop. */}
                    <div
                      ref={hwCarouselRef}
                      className="no-scrollbar"
                      onScroll={handleHwScroll}
                      style={{
                        display: 'flex',
                        width: '100%',
                        minWidth: 0,
                        overflowX: 'auto',
                        scrollSnapType: 'x mandatory',
                        scrollBehavior: 'smooth',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                      } as React.CSSProperties}
                    >
                      {openHomeworks.map((hw) => {
                        const doneCount = hw.exercises.filter((e) => e.status === 'done').length;
                        const total = hw.exercises.length;
                        const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
                        return (
                          <div
                            key={hw.homework_id}
                            style={{
                              flex: '0 0 100%',
                              width: '100%',
                              maxWidth: '100%',
                              minWidth: 0,
                              boxSizing: 'border-box',
                              padding: '0 4px',
                              scrollSnapAlign: 'start',
                            }}
                          >
                            <Link
                              to={`/student/homework/${hw.homework_id}`}
                              className="card h-full"
                              style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column' }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span style={{ fontWeight: 600, color: 'var(--text-dark)', fontSize: '0.95rem' }}>{hw.subject}</span>
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('student_dash.hw_done_count', { done: doneCount, total })}</span>
                              </div>
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '12px', flex: 1 }}>
                                {hw.themes.map((t) => (
                                  <span
                                    key={t}
                                    style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '999px', background: 'var(--primary-light)', color: 'var(--primary)' }}
                                  >
                                    {t}
                                  </span>
                                ))}
                              </div>
                              <div style={{ width: '100%', background: '#E5E7EB', borderRadius: '999px', height: '6px' }}>
                                <div style={{ width: `${pct}%`, background: 'var(--primary)', height: '6px', borderRadius: '999px', transition: 'width 0.3s' }} />
                              </div>
                            </Link>
                          </div>
                        );
                      })}
                    </div>

                    {/* Navigation arrows */}
                    {openHomeworks.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={prevHwSlide}
                          className="absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/90 border border-slate-200 text-slate-800 rounded-full shadow-sm hover:bg-slate-50 z-10"
                          aria-label={t('actions.previous')}
                        >
                          &#8592;
                        </button>
                        <button
                          type="button"
                          onClick={nextHwSlide}
                          className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/90 border border-slate-200 text-slate-800 rounded-full shadow-sm hover:bg-slate-50 z-10"
                          aria-label={t('actions.next')}
                        >
                          &#8594;
                        </button>
                      </>
                    )}
                  </div>

                  {/* Dot indicators */}
                  {openHomeworks.length > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '8px' }}>
                      {openHomeworks.map((_, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => scrollToHwSlide(idx)}
                          style={{
                            width: activeHwSlide === idx ? '20px' : '8px',
                            height: '8px', borderRadius: '4px', border: 'none',
                            background: activeHwSlide === idx ? 'var(--primary)' : '#D1D5DB',
                            transition: 'all 0.2s', cursor: 'pointer', padding: 0,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link to="/student/homework" style={{ textDecoration: 'none' }}>
                  <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '1.5rem' }}>📚</span>
                      <div>
                        <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-dark)', fontSize: '0.95rem' }}>{t('student_dash.my_homework')}</p>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>{t('student_dash.no_open_assignments')}</p>
                      </div>
                    </div>
                    <span style={{ color: 'var(--text-muted)', fontSize: '1.2rem' }}>›</span>
                  </div>
                </Link>
              )}

              {/* Add exam shortcut */}
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate('/student/exams')}
                style={{ width: '100%', margin: '20px 0 4px', fontSize: '0.9rem', textAlign: 'left', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <span>📅</span>
                <span>{t('student_dash.add_exam_cta')}</span>
              </button>

              {/* Today's Schedule */}
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '24px 0 12px' }}>
                {t('student_dash.day_schedule', { day: t(`student_timetable.days.${DAY_ABBR[todayString] ?? 'Mon'}`) })}
              </h2>

              {todaysClasses.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {todaysClasses.map((entry, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem', minWidth: '42px', fontWeight: 500 }}>
                        {entry.start_time}
                      </span>
                      <div className="subject-card" style={{ flex: 1, textAlign: 'left' }}>
                        {entry.subject}{entry.room ? ` · ${entry.room}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>
                  {t('student_dash.no_classes_today')}
                </p>
              )}
            </>
          )}

        </div>

        <BottomTabBar activeTab="Home" />
      </div>
    </div>
  );
}
